import axios from "axios";
import { Stock } from "@/models/stock";
import { currentLogosConfig } from "@/lib/logos";
import { isUsExchangeAlias, stockLookupExchanges, US_LOOKUP_EXCHANGE } from "@/lib/stock-url";
import { isEmpty } from "lodash";

export const getStockInfoOnClient = async (stockIdOrExchangeId: string, ticker?: string) => {
    try {
        const res = await axios.get<Stock>(`/API/v2/${ticker ? "s" : "id"}/${stockIdOrExchangeId}/${ticker ? `${ticker}/` : ""}`);

        if (res.data.currency === "GBP") {
            res.data.high_52_week = res.data.high_52_week * 100;
            res.data.low_52_week = res.data.low_52_week * 100;
            res.data.last_close = res.data.last_close * 100;
            res.data.previous_close = res.data.previous_close * 100;
        }
        return res.data;
    } catch (err) {
        throw err;
    }
};

/**
 * The stock API could not answer (5xx, timeout, network, or a body that is not
 * JSON). Distinct from "no such stock" so a page can answer 503 instead of 404:
 * a 404 during a backend outage tells Google to drop every stock page it crawls.
 */
export class StockBackendError extends Error {
    constructor(public status: number | null, message: string) {
        super(message);
        this.name = "StockBackendError";
    }
}

/** One `/API/v2/...` stock lookup. Resolves `{}` for "no such stock" (400/404), throws StockBackendError otherwise. */
const fetchStockOnServer = async (path: string): Promise<Stock> => {
    let res: Response;
    try {
        res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/API/v2/${path}`);
    } catch (err) {
        throw new StockBackendError(null, `stock lookup failed: ${err}`);
    }
    // The API answers an unknown stock with 400 "No data for stock".
    if (res.status === 400 || res.status === 404) return {} as Stock;
    if (!res.ok) throw new StockBackendError(res.status, `stock lookup answered ${res.status}`);
    try {
        return (await res.json()) ?? ({} as Stock);
    } catch (err) {
        throw new StockBackendError(res.status, `stock lookup returned a non-JSON body: ${err}`);
    }
};

/**
 * Server-side stock lookup by stock id, or by exchange + ticker.
 *
 * Resolves `{}` (check with `isEmpty`) when the stock does not exist and throws
 * StockBackendError when the API could not answer. Exchange lookups accept any
 * alias in `stockLookupExchanges` (`NasdaqGS`, `NASDAQ`, `nyse`, ...), because
 * the API does not resolve every exchange symbol it returns.
 */
export const getStockInfoOnServer = async (stockIdOrExchangeId: string, ticker?: string, trend?: boolean): Promise<Stock> => {
    const query = trend ? "?trend" : "";
    let stock = {} as Stock;
    if (ticker) {
        for (const exchange of stockLookupExchanges(stockIdOrExchangeId)) {
            stock = await fetchStockOnServer(`s/${encodeURIComponent(exchange)}/${encodeURIComponent(ticker)}/${query}`);
            if (!isEmpty(stock)) break;
        }
    } else {
        stock = await fetchStockOnServer(`id/${stockIdOrExchangeId}/${query}`);
    }
    if (stock.currency === "GBP") {
        stock.high_52_week = stock.high_52_week * 100;
        stock.low_52_week = stock.low_52_week * 100;
        stock.last_close = stock.last_close * 100;
        stock.previous_close = stock.previous_close * 100;
    }
    return stock;
};

/** Short-interest headline for a stock, as plain numbers for server-rendered text and share cards. */
export interface StockShortKeyData {
    /** Short interest, % of free float. */
    siPercentFreeFloat: number | null;
    /** Days to cover. */
    daysToCover: number | null;
    /** Average cost to borrow, % per year (all loans). */
    costToBorrow: number | null;
    /** The date the figures are for, YYYY-MM-DD. */
    asOf: string | null;
}

const SHORT_KEY_DATA_TTL_MS = 30 * 60 * 1000;
const SHORT_KEY_DATA_MAX_ENTRIES = 5000;
const shortKeyDataCache = new Map<string, { at: number; value: StockShortKeyData | null }>();

const isoDay = (date: Date) => date.toISOString().slice(0, 10);

/**
 * The customer API host. Not NEXT_PUBLIC_API_URL: production's app API host
 * (ortex-gui.ortex.com) answers 403 on /api/v11, and previews point at a dev
 * backend. ORTEX_SSR_API_URL overrides it for testing.
 */
const V11_API_ORIGIN = (process.env.ORTEX_SSR_API_URL || "https://api.ortex.com").replace(/\/$/, "");

/** The last row of a v11 enterprise history endpoint over the past ten days, or null. */
const fetchV11Latest = async (path: string, key: string, signal: AbortSignal): Promise<Record<string, unknown> | null> => {
    const to = new Date();
    const from = new Date(to.getTime() - 10 * 24 * 60 * 60 * 1000);
    const res = await fetch(`${V11_API_ORIGIN}/api/v11/enterprise/stock/${path}/?from_date=${isoDay(from)}&to_date=${isoDay(to)}`, {
        headers: { "Ortex-Api-Key": key },
        signal,
    });
    if (!res.ok) return null;
    const body = await res.json();
    const rows = Array.isArray(body?.data) ? body.data : [];
    return rows.length ? rows[rows.length - 1] : null;
};

/**
 * Short interest % of free float, days to cover and cost to borrow for the
 * server-rendered key-data summary and the share card, or null when they are
 * unavailable.
 *
 * Anonymous calls get obfuscated short data, and crawler access is granted on
 * the crawler's own verified IP, which a server-side request does not carry. So
 * this reads the v11 enterprise API with a dedicated server-only key
 * (`ORTEX_SSR_API_KEY`: the app-ssr@ortex.com service user, limited to the
 * "Enterprise Short Interest V11 - All" views, not credit-metered). The figures
 * are daily, so results are cached per instance for 30 minutes. Never throws and
 * never waits long: the summary is optional and must not slow or fail the page.
 */
export const getStockShortKeyDataOnServer = async (stock: Pick<Stock, "exchangesymbol" | "ticker">): Promise<StockShortKeyData | null> => {
    const key = process.env.ORTEX_SSR_API_KEY;
    if (!key || !stock?.ticker || !stock?.exchangesymbol) return null;
    // v11 resolves any US listing under the `US` venue, like /API/v2/s/.
    const namespace = isUsExchangeAlias(stock.exchangesymbol) ? US_LOOKUP_EXCHANGE : stock.exchangesymbol;
    const path = `${encodeURIComponent(namespace)}/${encodeURIComponent(stock.ticker)}`;
    const cached = shortKeyDataCache.get(path);
    // A miss (no data, or a timeout) is retried sooner than a hit is refreshed.
    if (cached && Date.now() - cached.at < (cached.value ? SHORT_KEY_DATA_TTL_MS : SHORT_KEY_DATA_TTL_MS / 6)) return cached.value;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 1500);
    try {
        const [si, ctb] = await Promise.all([
            fetchV11Latest(`${path}/short/short_interest`, key, controller.signal).catch(() => null),
            fetchV11Latest(`${path}/short/ctb`, key, controller.signal).catch(() => null),
        ]);
        const num = (value: unknown) => (typeof value === "number" && Number.isFinite(value) ? value : null);
        const value: StockShortKeyData | null =
            si || ctb
                ? {
                      siPercentFreeFloat: num(si?.siPctFreeFloat),
                      daysToCover: num(si?.daysToCover),
                      costToBorrow: num(ctb?.costToBorrowAll),
                      asOf: typeof (si?.date ?? ctb?.date) === "string" ? ((si?.date ?? ctb?.date) as string) : null,
                  }
                : null;
        if (shortKeyDataCache.size >= SHORT_KEY_DATA_MAX_ENTRIES) shortKeyDataCache.clear();
        shortKeyDataCache.set(path, { at: Date.now(), value });
        return value;
    } catch {
        return null;
    } finally {
        clearTimeout(timer);
    }
};

export const getStockInfoByTicker = async (ticker: string): Promise<Stock> => {
    try {
        const res = await axios.get(`/API/v2/s/US/${ticker}/`);

        if (res.data.currency === "GBP") {
            res.data.high_52_week = res.data.high_52_week * 100;
            res.data.low_52_week = res.data.low_52_week * 100;
            res.data.last_close = res.data.last_close * 100;
            res.data.previous_close = res.data.previous_close * 100;
        }
        return res.data;
    } catch (err) {
        throw err;
    }
};

/**
 * Look up a Stock by Security id — used where the caller already has
 * the security_id from another payload (e.g. a universal flow row
 * carries it) so we skip the /find/ search. Hits the same endpoint
 * as the ticker / id lookups and returns the same Stock shape.
 */
export const getStockInfoBySecurityId = async (securityId: number): Promise<Stock> => {
    const res = await axios.get<Stock>(`/API/v2/sid/${securityId}/`);
    if (res.data.currency === "GBP") {
        res.data.high_52_week = res.data.high_52_week * 100;
        res.data.low_52_week = res.data.low_52_week * 100;
        res.data.last_close = res.data.last_close * 100;
        res.data.previous_close = res.data.previous_close * 100;
    }
    return res.data;
};

export const getStockLogo = async (stockId: number) => {
    try {
        const res = await axios.get<{ url: string; mimeType: string }>(`/API/v2/id/${stockId}/logo`);

        return res.data.url;
    } catch (err) {
        throw err;
    }
};

export const getNoBackgroundLogos = async (): Promise<number[]> => {
    try {
        const res = await axios.get<{ securityIds: number[] }>(`/interface/api/security/logos/no_background`);

        return res.data.securityIds;
    } catch (err) {
        throw err;
    }
};

export const getMissingLogo = async (security_id: number) => {
    try {
        if (!security_id) return;
        // Only the legacy origin is filled by asking web (which runs the FactSet importer into the
        // logo.ortex.com bucket). On the edge the logo service records the miss itself and its
        // refresh sweep sources the logo; asking web here only churns the legacy bucket.
        if (currentLogosConfig().source === "edge") return;

        const res = await axios.get(`/interface/api/security/${security_id}/logo`);

        return res.data;
    } catch (err) {
        throw err;
    }
};

export const getStockCommentary = async (exchange: string, stock: string) => {
    try {
        const res = await axios.get(`/API/v2/s/${exchange}/${stock}/stock_commentary`);

        return res.data;
    } catch (err) {
        throw err;
    }
};
